import _ from 'lodash';
import { useState, useEffect, useMemo, createContext } from "react";
import { useNavigate } from 'react-router-dom';
import { DragDropContext } from "@hello-pangea/dnd";
import { useMsal } from '@azure/msal-react';
import KanbanAPI from '../../api/kanban-board/kanban-api-factory';
import { Columns } from '../kanban/columns';
import { arrayAny } from '../../utils/arrayUtils';
import { generateObjectId } from '../../utils/idUtils';

export const MaintenanceKanbanContext = createContext();

export const PropertyMaintenanceTab = ({ propertyDocument, saveProperty }) => {

    const { instance, inProgress, accounts } = useMsal();
    const navigate = useNavigate();

    const [maintenanceBoard, setMaintenanceBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [movedItem, setMovedItem] = useState(null);
    const [render, rerender] = useState(false);

    const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const propertyId = propertyDocument.id;
    const companyId = propertyDocument.property.landlordOrOwner.parent.id;

    useEffect(() => {
        const loadMaintenanceData = async () => {
            try {
                const propertyProjects = await kanbanAPI.getProjectsByProperty(companyId, propertyId, 'Maintenance');
                // Find the maintenance board for this property
                const board = propertyDocument.property?.selectedProject ? 
                                                            propertyProjects.find(p => p.id === propertyDocument.property.selectedProject) : 
                                                            propertyProjects[0];
                
                if(!propertyDocument.property?.selectedProject && !!board){
                    propertyDocument.property.selectedProject = board.id;
                    const property = { ...propertyDocument.property };
                    await saveProperty(property);
                }
                    

                if (board && board.columns) {
                    // Order items in each column
                    const orderedBoard = {
                        ...board,
                        columns: board.columns.map(column => ({
                            ...column,
                            items: _.orderBy(column.items || [], ['order'], ['asc'])
                        }))
                    };

                    setMaintenanceBoard(orderedBoard);
                } else {
                    setMaintenanceBoard(null);
                }
            } catch (error) {
                console.error("Error loading maintenance data:", error);
                setMaintenanceBoard(null);
            } finally {
                setLoading(false);
            }
        };

        loadMaintenanceData();
    }, [kanbanAPI, companyId, propertyId, propertyDocument.property, saveProperty]);

    const handleOnDragStart = ({ source }) => {
        const sourceColumn = maintenanceBoard.columns.find(t => t.name === source.droppableId);
        const tile = sourceColumn.items[source.index];
        tile.saving = true;
        setMovedItem(tile);
    };

    const handleDragEnd = async ({ destination, source }) => {
        if (!destination || !movedItem) {
            return;
        }

        if (destination.index === source.index && destination.droppableId === source.droppableId) {
            setMovedItem(current => current.saving = false);
            return;
        }

        setMaintenanceBoard(current => {
            const sourceColumn = current.columns.find(t => t.name === source.droppableId);
            const destinationColumn = current.columns.find(t => t.name === destination.droppableId);
            const isLastColumn = _.maxBy(current.columns, c => c.displayOrder).columnId === destinationColumn.columnId;

            movedItem.order = destination.index;
            movedItem.destinationColumnId = destinationColumn.columnId;

            // Handle completion status based on column
            if (sourceColumn !== destinationColumn) {
                if (isLastColumn) {
                    movedItem.completed = new Date().toISOString();
                    movedItem.status = "Closed";
                } else {
                    movedItem.completed = null;
                    movedItem.status = destinationColumn.name === "In Progress" ? "In Progress" : "Open";
                }
            }

            if (sourceColumn !== destinationColumn) {
                sourceColumn.items.splice(sourceColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            } else {
                destinationColumn.items.splice(destinationColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            }

            sourceColumn.items.forEach((item, index) => {
                item.order = index;
            });

            destinationColumn.items.forEach((item, index) => {
                item.order = index;
            });

            return current;
        });

        const data = await kanbanAPI.upsertTile(movedItem, "Maintenance");
        updateBoard(data);
        setMovedItem(null);
        rerender(!render);
    };

    const updateBoard = newData => {
        if (newData && arrayAny(Object.keys(newData))) {
            if (!!movedItem) {
                for (const column of newData.columns) {
                    const movingTile = column.items.find(t => t.id === movedItem.id);
                    if (!!movingTile) {
                        movingTile.saving = false;
                        setMovedItem(movingTile);
                        break;
                    }
                }
            }
            setMaintenanceBoard(newData);
        }
    };

    const localUpsertTile = async tile => {
        const data = await kanbanAPI.upsertTile(tile, "Maintenance");
        updateBoard(data);
    };

    const localSetData = (updateMethod) => {
        setMaintenanceBoard(current => {
            if (!current) return current;
            return updateMethod(current);
        });
    };

    const localAddSubTask = async (project, tileId, newTask) => {
        // Find and update tile locally, then upsert
        setMaintenanceBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile) {
                newTask.subTaskId = generateObjectId();
                newTask.order = tile.subTasks?.length || 0;
                tile.subTasks = tile.subTasks || [];
                tile.subTasks.push(newTask);
            }
            return current;
        });
        rerender(!render);
    };

    const localDeleteSubTask = async (project, tileId, subTask) => {
        // Find and update tile locally, then upsert
        setMaintenanceBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                tile.subTasks = tile.subTasks.filter(st => st.subTaskId !== subTask.subTaskId);
            }
            return current;
        });
        rerender(!render);
    };

    const localUpdateSubTask = async (project, tileId, updatedTask) => {
        // Find and update tile locally, then upsert
        setMaintenanceBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                const index = tile.subTasks.findIndex(st => st.subTaskId === updatedTask.subTaskId);
                if (index !== -1) {
                    tile.subTasks[index] = updatedTask;
                }
            }
            return current;
        });
        rerender(!render);
    };

    const createMaintenanceBoard = async () => {
        try {
            setCreating(true);
            const companyId = propertyDocument.property.landlordOrOwner.parent.id;
            const propertyId = propertyDocument.id;
            const boardName = propertyDocument.property.name;

            const newBoard = await kanbanAPI.addProject(boardName, companyId, propertyId);
            setMaintenanceBoard(newBoard);
        } catch (error) {
            console.error("Error creating maintenance board:", error);
        } finally {
            setCreating(false);
        }
    };

    const addMaintenanceRequest = async () => {
        try {
            setCreating(true);
            const firstColumn = maintenanceBoard.columns[0];
            const newTile = {
                name: "New Maintenance Request",
                description: "",
                order: firstColumn.items.length,
                boardId: maintenanceBoard.id,
                columnId: firstColumn.columnId,
                subTasks: [],
                tags: [],
                templateId: null,
                completedBy: null,
                dueDate: null,
                completed: null,
                priority: "Low",
                taskType: "Maintenance"
            };

            const data = await kanbanAPI.upsertTile(newTile, "Maintenance");
            updateBoard(data);
        } catch (error) {
            console.error("Error adding maintenance request:", error);
        } finally {
            setCreating(false);
        }
    };

    if (loading && !maintenanceBoard) {
        return (
            <div className="tab-pane fade" id="nav-pills-tab-maintenance">
                <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <MaintenanceKanbanContext.Provider value={{
            upsertTile: localUpsertTile,
            markTileToDelete: () => { }, // Disabled for maintenance view
            setData: localSetData,
            deleteSubTask: localDeleteSubTask,
            addSubTask: localAddSubTask,
            updateSubTask: localUpdateSubTask,
            moveColumn: () => { },
            deleteColumn: () => { }
        }}>
            <div className="tab-pane fade" id="nav-pills-tab-maintenance">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mt-10px">Maintenance Board</h3>
                    <button
                        className="btn btn-primary"
                        onClick={maintenanceBoard ? addMaintenanceRequest : createMaintenanceBoard}
                        disabled={creating}
                    >
                        {creating ? 'Creating...' : maintenanceBoard ? 'Add Request' : 'Create Maintenance Board'}
                    </button>
                </div>

                <div className="row">
                    {maintenanceBoard && maintenanceBoard.columns ? (
                        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleOnDragStart}>
                            <Columns
                                project={maintenanceBoard}
                                stack={false}
                                showTaskDelete={false}
                                showPriorityBadge={true}
                                showViewDetailsButton={true}
                                onViewDetails={(tile) => navigate(`/property/${propertyDocument?.id || 'default'}/maintenance/${tile.id}`)}
                            />
                        </DragDropContext>
                    ) : (
                        <div className="col-12">
                            <div className="alert alert-info">
                                No maintenance board found for this property.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </MaintenanceKanbanContext.Provider>
    );
}